/**
 * AR Vision Demo — globally-registered navigation tools.
 *
 * THROWAWAY DEMO CODE. Some scripted beats navigate to a page the user is NOT
 * currently on (e.g. scene 10 "show payment disputes that need approval" fires
 * from the workflow/summary surface and must jump to the dispute thread). Those
 * pages only register their `useArAgentPage` tools once mounted, so a navigation
 * tool that targets them must be registered up-front from an always-mounted host.
 *
 * This hook registers ONLY into the demo-script registry (it does NOT touch the
 * live agent module, so it never clobbers the per-page `currentPage`/tool set).
 * It is called once from `ArDemoControls`, which is mounted for the whole demo
 * session inside the router. Delete with frontend/src/demo/ar.
 */

import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import paths from '@/paths';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
import { registerArDemoTools } from './demo-script';
import { getArDemoState } from './fixture-store';
import {
  REMINDERS_DEMO_PATH,
  INTEL_CORPUS_DEMO_PATH,
  INTEL_INTELLIGENCE_DEMO_PATH,
  INTEL_SEND_DEMO_PATH,
} from './demo-routes';
import { REPORT_CUSTOMER } from './fixture-seed';
import { pushReportDemoView } from './report-view-signal';
// AR Email Intelligence Demo — checkpoints fired from this always-mounted host
// so scripted prompts can navigate/apply from any page (a page's own
// useArAgentPage registration only exists once that page is mounted). Scene 0
// corpus-learning (E1/GEN-2263) + Scenes 4–5 intelligence/recommendations
// (E3/GEN-2265) + Scene 6 send/reply payoff (E4/GEN-2266). Delete with
// frontend/src/demo/ar/email-intel.
import {
  applyCorpusLearned,
  applyCustomerIntelligenceShown,
  applyRecommendationApplied,
  applyEmailSent,
  applyReplyReceived,
} from './email-intel/checkpoints';
import { INTEL_SEND_ANIMATION_MS } from './email-intel/payoff/payoff-copy';

/** The Custom Aging Report exploration page (no path constant for /new). */
const REPORTS_DEMO_PATH = '/accounts-receivable/reports/new';

export function useArDemoGlobalNavTools(): void {
  const navigate = useNavigate();

  const tools = useMemo<FrontendToolDefinition[]>(() => {
    const noArgsSchema = z.toJSONSchema(z.object({})) as Record<
      string,
      unknown
    >;
    // Each scripted nav beat fires from whatever page the presenter is on, so
    // every "navigate to X" tool must be registered up-front here (a page's own
    // useArAgentPage registration only exists once that page is mounted).
    const navTool = (
      name: string,
      description: string,
      execute: () => unknown,
    ): FrontendToolDefinition => ({
      name,
      description,
      awaitable: false,
      schema: { name, description, parameters: noArgsSchema, awaitable: false },
      execute,
    });

    return [
      navTool(
        'ar_navigate_invoice',
        'Open the invoice detail for the invoice that was just sent to the customer.',
        () =>
          navigate({
            to: paths.authenticated.accountsReceivable.invoice,
            params: { arInvoiceId: getArDemoState().flagship.invoiceId },
            search: true,
            viewTransition: true,
          }),
      ),
      navTool(
        'ar_navigate_customer_profile',
        "Open the flagship customer's Collections Profile, showing the shipping-terms rule that controls when invoices are sent.",
        () =>
          navigate({
            to: '/accounts-receivable/collections/$customerId',
            params: { customerId: getArDemoState().flagship.customerId },
          }),
      ),
      navTool('ar_navigate_summary', 'Open the AR summary dashboard.', () =>
        navigate({ to: paths.authenticated.accountsReceivable.summary }),
      ),
      navTool(
        'ar_navigate_reminders',
        'Open the list of reminder emails the automation has sent.',
        () => navigate({ to: REMINDERS_DEMO_PATH }),
      ),
      // AR Email Intelligence Demo (GEN-2263) — Scene 0 (E1). The cold-open
      // lead beat; registered globally because it fires before the corpus page
      // is mounted. Opens the corpus gate, then navigates to the surface (which
      // re-registers the same tool name via useArAgentPage once mounted).
      navTool(
        'intel_show_corpus_learning',
        "Show what Gently learned from the user's email inbox — corpus ingest stats and the per-customer comms profiles it built.",
        () => {
          applyCorpusLearned();
          return navigate({ to: INTEL_CORPUS_DEMO_PATH });
        },
      ),
      navTool(
        'ar_navigate_overdue_customer',
        'Open the collections profile for the four-weeks-overdue customer.',
        () =>
          navigate({
            to: '/accounts-receivable/collections/$customerId',
            params: { customerId: getArDemoState().overdueCustomer.customerId },
          }),
      ),
      navTool(
        'ar_navigate_dispute_thread',
        'Open the Granite Peak Distributors short-pay dispute collection thread.',
        () => {
          const { disputeCustomer } = getArDemoState();
          return navigate({
            to: '/accounts-receivable/collections/$customerId/threads/$threadId',
            params: {
              customerId: disputeCustomer.customerId,
              threadId: disputeCustomer.threadId,
            },
            search: { from: 'threads' },
          });
        },
      ),
      // AR Email Intelligence Demo (E3 / GEN-2265) — Scene 4: open Granite Peak's
      // auto-built Customer Intelligence panel (learned facts + provenance) and
      // mark the corpus gate open.
      navTool(
        'intel_show_customer_intelligence',
        'Open the auto-built Customer Intelligence panel for Granite Peak Distributors — the facts learned from their email history, each linked to its source email.',
        () => {
          applyCustomerIntelligenceShown();
          return navigate({ to: INTEL_INTELLIGENCE_DEMO_PATH });
        },
      ),
      // AR Email Intelligence Demo (E3 / GEN-2265) — Scene 5: apply the learned
      // recommendations to the Granite Peak draft (flips each
      // `recommendation.applied`); also surfaces the panel so the applied state
      // is visible. E4's draft reads the applied flag — no cross-component reach.
      navTool(
        'intel_apply_recommendation',
        'Apply the email-intelligence recommendations for Granite Peak to the outgoing draft — pre-empt the freight dispute with the POD and CC Maria, who actually releases payment.',
        () => {
          applyRecommendationApplied();
          return navigate({ to: INTEL_INTELLIGENCE_DEMO_PATH });
        },
      ),
      // AR Email Intelligence Demo (GEN-2266) — Scene 6 payoff tools. Registered
      // up-front here because Scene 5 leaves the presenter on the intelligence
      // surface, so the send/reply beat must fire from any page. `intel_send_email`
      // is awaitable so its send animation completes before the reply lands.
      {
        name: 'intel_send_email',
        description:
          "Send the Granite Peak follow-up composed in Sarah's voice (POD attached, Maria CC'd) and open the payoff surface.",
        awaitable: true,
        schema: {
          name: 'intel_send_email',
          description:
            "Send the Granite Peak follow-up composed in Sarah's voice (POD attached, Maria CC'd) and open the payoff surface.",
          parameters: noArgsSchema,
          awaitable: true,
        },
        execute: async () => {
          await navigate({
            to: INTEL_SEND_DEMO_PATH,
            viewTransition: true,
          });
          applyEmailSent();
          const reduceMotion =
            typeof window !== 'undefined' &&
            !!window.matchMedia &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          if (!reduceMotion) {
            await new Promise<void>((resolve) =>
              window.setTimeout(resolve, INTEL_SEND_ANIMATION_MS),
            );
          }
        },
      },
      navTool(
        'intel_show_reply',
        'Reveal the warm inbound reply from Granite Peak after the email is sent.',
        () => {
          applyReplyReceived();
        },
      ),
      navTool(
        'ar_navigate_reports',
        'Open the Custom Aging Report so the user can explore AR data — filter, sort, and group open invoices.',
        () => navigate({ to: REPORTS_DEMO_PATH }),
      ),
      navTool(
        'ar_report_overdue_oldest_first',
        'Open the Custom Aging Report and show every overdue invoice in a flat list sorted oldest-first (most days overdue at the top).',
        () => {
          pushReportDemoView({
            viewMode: 'flat',
            flatSortBy: 'daysOut',
            flatSortDir: 'desc',
          });
          return navigate({ to: REPORTS_DEMO_PATH });
        },
      ),
      navTool(
        'ar_report_drill_customer',
        "Open the report-only customer's Collections Profile to investigate their payment pattern (declining trend, new AP contact).",
        () =>
          navigate({
            to: '/accounts-receivable/collections/$customerId',
            params: { customerId: REPORT_CUSTOMER.customerId },
          }),
      ),
    ];
  }, [navigate]);

  useMemo(() => {
    registerArDemoTools('ar-demo-global-nav', tools);
  }, [tools]);
}
